/* ---------------------------------------------------------------------------
   ChatbotInterface – React version aligned with chat‑widget‑v2
   --------------------------------------------------------------------------- */

// React & third‑party
import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  updateDoc,
  doc,
  setDoc,
  arrayUnion,
  getDoc,
} from 'firebase/firestore';
import { Send, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { User } from 'lucide-react';

// Internal components & utils
import { db } from '../firebase';
import Sidebar from './Sidebar';
import MarkdownRenderer from '../utils/MarkdownRenderer';
import { BotIcon } from '../icons/Icons';
import { DISCLAIMER_TEXT } from './DisclaimerMessage';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Configuration & constants                                               */
/* ────────────────────────────────────────────────────────────────────────── */

// Cloud‑run endpoint (overrideable via env for dev)
const API_URL =
  process.env.REACT_APP_JUDICIARY_API_URL || 'https://faq-zxq5yf5dea-uc.a.run.app';
  // process.env.REACT_APP_JUDICIARY_API_URL || 'http://127.0.0.1:5001/kolokolo-chat/us-central1/faq';
// Private header value – keep in env for prod builds
const WIDGET_KEY =
  process.env.REACT_APP_JUDICIARY_WIDGET_KEY || 'jud1c1ary-w1dg3t-k3y-2025';

// Maintenance toggle & links (mirrors widget v2)
const UNDER_MAINTENANCE = process.env.REACT_APP_UNDER_MAINTENANCE || false;
const MAINTENANCE_LINK_SECTIONS = [
  {
    title: 'COURT FORMS',
    items: [
      {
        text: 'First Circuit',
        url: 'https://www.courts.state.hi.us/self-help/courts/forms/oahu/oahu_first_circuit_forms',
      },
      {
        text: 'Second Circuit',
        url: 'https://www.courts.state.hi.us/self-help/courts/forms/maui/maui_second_circuit_forms',
      },
      {
        text: 'Third Circuit',
        url: 'https://www.courts.state.hi.us/self-help/courts/forms/hawaii/hawaii_third_circuit_forms',
      },
      {
        text: 'Fifth Circuit',
        url: 'https://www.courts.state.hi.us/self-help/courts/forms/kauai/kauai_fifth_circuit_forms',
      },
      {
        text: 'Statewide Forms',
        url: 'https://www.courts.state.hi.us/self-help/courts/forms/court_forms',
      },
    ],
  },
  {
    title: 'COURT LOCATIONS AND PHONE NUMBERS',
    items: [
      {
        text: 'First Circuit',
        url: 'https://www.courts.state.hi.us/general_information/contact/oahu',
      },
      {
        text: 'Second Circuit',
        url: 'https://www.courts.state.hi.us/general_information/contact/maui',
      },
      {
        text: 'Third Circuit',
        url: 'https://www.courts.state.hi.us/general_information/contact/hawaii',
      },
      {
        text: 'Fifth Circuit',
        url: 'https://www.courts.state.hi.us/general_information/contact/kauai',
      },
    ],
  },
  {
    title: 'eCOURT KOKUA/CASE LOOKUP',
    url: 'https://www.courts.state.hi.us/legal_references/records/jims_system_availability',
  },
  {
    title: 'eREMINDER FOR COURT DATES',
    url: 'https://www.courts.state.hi.us/ereminder',
    items: [
      {
        text: 'Register',
        url: 'http://jimspss1.courts.state.hi.us:8080/eReminderExternal/login.jspx',
      },
    ],
  },
  {
    title: 'JEFS/e-FILING',
    url: 'https://www.courts.state.hi.us/legal_references/efiling',
    items: [
      {
        text: 'JEFS Login',
        url: 'https://jimspss1.courts.state.hi.us/JIMSExternal/login.iface',
      },
      {
        text: 'JEFS Login and Self-Registration Information',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2017/01/JEFS_Login_and_Self_Registration.pdf',
      },
      {
        text: 'JEFS Guide for Subscribers',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2021/01/Guide-to-Document-Subscribers.pdf',
      },
      {
        text: 'JEFS Self-Represented Litigants Guide',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2023/03/JEFS-SRL-Guide-3-2023-ADA.pdf',
      },
    ],
  },
  {
    title: 'JURY DUTY SERVICE',
    items: [
      {
        text: 'Jury Duty FAQs',
        url: 'https://www.courts.state.hi.us/general_information/jury/jury_service_faqs#one',
      },
      {
        text: 'Do I have Jury Duty?',
        url: 'https://www.courts.state.hi.us/general_information/jury/jury_prelogin',
      },
    ],
  },
  {
    title: 'PAY FOR TRAFFIC FINE',
    url: 'https://www.courts.state.hi.us/self-help/traffic/the_etraffic_hawaii_website',
  },
  {
    title: 'REMOTE HEARINGS (ZOOM) INFORMATION',
    url: 'https://www.courts.state.hi.us/remote-court-hearings-via-zoom-or-webex',
    items: [
      {
        text: 'First Circuit Court Zoom Numbers',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2022/10/1DC_Zoom_flyer_with_civil.pdf',
      },
      {
        text: 'Second Circuit Court Zoom Numbers',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2024/07/2024-22-07-Second-Circuit-Remote-Hearings.revised-071524-1.pdf',
      },
      {
        text: 'Third Circuit Court Zoom Numbers',
        url: 'https://www.courts.state.hi.us/wp-content/uploads/2021/05/Third-Circuit-Zoom-ID-Meeting-Numbers.pdf',
      },
      {
        text: "Do's and Don'ts of Remote Hearings",
        url: 'https://www.youtube.com/embed/VLD-kWLfjq4',
      },
    ],
  },
  {
    title: 'REQUEST A COURT INTERPRETER',
    url: 'https://www.courts.state.hi.us/services/how_to_request_an_interpreter_for_court_proceeding',
  },
  {
    title: 'SELF HELP CENTERS',
    url: 'https://www.courts.state.hi.us/general_information/access_to_justice_rooms_self_help_centers',
  },
];

// Rate‑limiting parameters
const RATE_LIMITS = {
  cooldownMs: 500,
  maxPerMinute: 15,
};

// Processing‑status → user‑visible string
const STATUS_TEXT = {
  classifying: 'Analyzing your question',
  retrieving: 'Finding relevant information',
  generating: 'Generating your answer',
};

// Updated disclaimer (from widget v2)
const FULL_DISCLAIMER = `Aloha and welcome to the Hawai'i State Judiciary Chatbot. Before we begin, please review and agree to the following terms:


By using the Hawai'i State Judiciary website ("Website") and KolokoloChat ("Chatbot"), you acknowledge and agree to the following terms:
1. **General Information Only (This is not legal advice):**
  The information provided by the Website and the Chatbot is for general informational purposes only and does not constitute legal advice.  It is not a substitute for professional legal advice from an attorney.  The Chatbot is designed to assist with general inquiries and navigation of the Website but does not offer personalized legal solutions.  You should consult with an attorney for legal advice regarding your individual situation.

2. **User Responsibility for Actions Taken:**
  The Hawai'i State Judiciary assumes no liability for any actions taken based on information obtained from the Website and the Chatbot.  We strive to provide accurate information, but errors may occur.  Users are responsible for verifying any information obtained from the Chatbot with official resources or legal professionals before taking any action.

3. **Service Modifications:**
  The Hawai'i State Judiciary reserves the right to modify, suspend, or discontinue the Chatbot service at any time.

4. **Do Not Share Your Personally Identifiable Information (PII):**
  The Hawai'i State Judiciary is not responsible for any privileged, confidential, or personally identifiable information (PII) that users submit to the Chatbot.  While we take precautions to implement security measures for the Chatbot, PII and privileged or confidential information provided by users through the Chatbot are not within the Judiciary's scope of liability.  The Chatbot is not a secure platform for transmitting sensitive or confidential information.  To protect your privacy, please submit only general questions related to your situation and do not share any PII or other sensitive, privileged, or confidential information with the Chatbot.

5. **Data Collection:**
Please note that Chatbot data may be retained by the Judiciary to gather information for improving this service. 

6. **Prohibited Activities:**
  By using the Website and Chatbot, you agree not to engage in any unauthorized or inappropriate use of the Chatbot that deviates from its intended purpose.  This includes, but is not limited to, attempts to exploit, disrupt, or otherwise abuse the Chatbot's functionality through methods such as brute force attacks or any other form of abusive activity. Such actions are strictly prohibited and may result in immediate termination of access to the Chatbot and potential enforcement action.

7. **Agreement to Terms:**
  Do you agree to these terms? (Please respond with **'Agree'** or **'Disagree'**)`;

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helper components                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

const ProcessingAnimation = ({ status }) => {
  const message = STATUS_TEXT[status] || 'Processing';
  return (
    <div className="flex flex-col items-start space-y-2 p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
      <div className="text-sm text-gray-600">{message}</div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

const ChatbotInterface = () => {
  /* ··· local state ··· */
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [uuid, setUuid] = useState('');
  const [ipAddr, setIpAddr] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [feedback, setFeedback] = useState({});

  // rate‑limiting bookkeeping
  const lastSentRef = useRef(0);
  const sentCountRef = useRef(0);
  const resetTimeoutRef = useRef(null);

  const messagesEndRef = useRef(null);

  /* ─ lifecycle ─ */
  useEffect(() => {
    setUuid(uuidv4());
    if (UNDER_MAINTENANCE) {
      setMessages([{ type: 'bot', content: maintenanceMarkup() }]);
    } else {
      setMessages([{ type: 'bot', content: FULL_DISCLAIMER }]);
    }
    // Fetch and cache the visitor’s public IP (once per page-load)
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then(({ ip }) => {
        setIpAddr(ip);
        console.log('[IP] Detected client IP:', ip);
      })
      .catch((err) => console.warn('Unable to get IP', err));
    
    
  }, []);



  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ─ helpers ─ */
  const maintenanceMarkup = () => {
    return (
      `<strong>Our chat service is currently undergoing maintenance.</strong><br/>` +
      `Please use the following resources in the meantime:<br/>` +
      MAINTENANCE_LINK_SECTIONS.map((sec) => {
        const items = sec.items
          ? `<ul>${sec.items
              .map((i) => `<li><a href="${i.url}" target="_blank" rel="noopener noreferrer">${i.text}</a></li>`)  
              .join('')}</ul>`
          : '';
        const mainLink = sec.url
          ? `<p><a href="${sec.url}" target="_blank" rel="noopener noreferrer">${sec.title}</a></p>`
          : '';
        return `<h3>${sec.title}</h3>${mainLink}${items}`;
      }).join('')
    );
  };

  const logToFirestore = async (data, msgSeq) => {
    try {
      const ref = doc(db, 'logs', data.query_uuid);
      const snap = await getDoc(ref);
      const entry = {
        ...data,
        msgSeq,
        timestamp: new Date().toISOString(),
        origin: window.location.origin,
        host: window.location.hostname,
      };
      if (!snap.exists()) {
        await setDoc(ref, { sessionId: uuid, responses: [entry] });
      } else {
        await updateDoc(ref, { responses: arrayUnion(entry) });
      }
    } catch (err) {
      console.error('Firestore logging error:', err);
    }
  };

  const rateLimitOk = () => {
    const now = Date.now();
    if (now - lastSentRef.current < RATE_LIMITS.cooldownMs) return false;
    if (sentCountRef.current >= RATE_LIMITS.maxPerMinute) return false;
    // update counters
    lastSentRef.current = now;
    sentCountRef.current += 1;
    if (!resetTimeoutRef.current) {
      resetTimeoutRef.current = setTimeout(() => {
        sentCountRef.current = 0;
        resetTimeoutRef.current = null;
      }, 60000);
    }
    return true;
  };

  const addMessage = (msg) => setMessages((prev) => [...prev, msg]);

  /* ─ event handlers ─ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (UNDER_MAINTENANCE) return; // ignore during maintenance

    // rate‑limit
    if (!rateLimitOk()) {
      addMessage({ type: 'bot', content: "Please slow down. You're sending messages too quickly." });
      setInput('');
      return;
    }

    const userContent = input.trim();

    // Log the IP + query to the dev console
    console.log('[QUERY]', { ip: ipAddr, query: userContent });


    addMessage({ type: 'user', content: userContent });
    setInput('');

    // disclaimer gate
    if (!agreed) {
      const low = userContent.toLowerCase();
      if (low === 'agree') {
        setAgreed(true);
        addMessage({
          type: 'bot',
          content:
            'Thank you for agreeing to the terms. My name is KolokoloChat and I am here to help provide general Hawaii State Judiciary information. How can I assist you today?',
        });
      } else if (low === 'disagree') {
        addMessage({
          type: 'bot',
          content:
            "I'm sorry, but I cannot provide any assistance unless you agree to the terms. Please type 'Agree' if you wish to proceed, or close this chat if you do not agree.",
        });
      } else {
        addMessage({ type: 'bot', content: "Please respond with 'Agree' or 'Disagree' to the terms." });
      }
      return;
    }

    // send to backend
    setIsLoading(true);
    setProcessingStatus(null);

    try {
      const resp = await fetch(`${API_URL}/faq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Judiciary-Widget-Key': WIDGET_KEY,
          'X-Client-Origin': window.location.origin,
        },
        body: JSON.stringify({ query: userContent, query_uuid: uuid, stream: true }),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let boundary;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, boundary).trim();
          buffer = buffer.slice(boundary + 2);
          if (!raw.startsWith('data:')) continue;
          const jsonStr = raw
            .split('\n')
            .map((l) => l.replace(/^data:\s*/, ''))
            .join('\n');
          let evt;
          try {
            evt = JSON.parse(jsonStr);
          } catch (err) {
            console.error('Bad SSE JSON', err, jsonStr);
            continue;
          }
          handleSseEvent(evt);
          if (evt.status === 'complete' || evt.status === 'error') {
            reader.cancel();
            break;
          }
        }
      }
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setProcessingStatus(null);
      addMessage({ type: 'bot', content: "I'm sorry, an error occurred while processing your request." });
    }
  };

  const handleSseEvent = async (evt) => {
    switch (evt.status) {
      case 'started':
      case 'classifying':
      case 'retrieving':
      case 'generating':
        setProcessingStatus(evt.status);
        break;
      case 'complete': {
        setIsLoading(false);
        setProcessingStatus(null);
        const seq = messages.filter((m) => m.type === 'bot').length + 1;
        await logToFirestore(evt, seq);
        const botContent =
          evt.response?.answer || evt.response?.error || "I'm sorry, I couldn't process your request.";
        addMessage({ type: 'bot', content: botContent, fullResponse: evt, msgSeq: seq });
        break;
      }
      case 'error':
        setIsLoading(false);
        setProcessingStatus(null);
        addMessage({ type: 'bot', content: evt.message || 'An unknown error occurred.' });
        break;
      default:
        break;
    }
  };

  const toggleDetails = (i) => setExpanded(expanded === i ? null : i);

  /* ─ render helpers ─ */
  const isSpecial = (content) => content === FULL_DISCLAIMER || content.startsWith('Thank you for agreeing');

  /* ─ JSX ─ */
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-80">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center">
          <img src="BlueSeal_gray_218_bg.jpg" alt="Hawaii State Judiciary" className="h-24 mr-8" />
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Judiciary Assistant</h1>
            <p className="text-sm text-gray-600">How can I help you today?</p>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-2xl min-w-[200px] rounded-lg p-4 text-left ${
                    m.type === 'user' ? 'bg-blue-600 text-white ml-8' : 'bg-white shadow-sm border border-gray-200 mr-8'
                  }`}
                >
                  {m.type === 'bot' ? (
                    <div className="flex items-left mb-2">
                      <BotIcon className="w-6 h-6 text-blue-600 mr-2" />
                      <span className="font-semibold text-gray-700">Judiciary Assistant</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-start mb-2">
                      <User className="w-6 h-6 text-white mr-1" />
                    </div>
                  )}
                  <div className="prose max-w-none font-['Montserrat']">
                    <MarkdownRenderer>{m.content}</MarkdownRenderer>
                  </div>

                  {m.type === 'bot' && !isSpecial(m.content) && (
                    <div className="mt-4 flex justify-between items-center border-t border-gray-100 pt-3">
                      <button
                        onClick={() => toggleDetails(i)}
                        className="text-sm text-blue-600 flex items-center hover:underline"
                      >
                        {expanded === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <span className="ml-1">View Details</span>
                      </button>
                      {/* feedback buttons (ui only; handleFeedback row removed for brevity) */}
                      <div className="flex space-x-2 opacity-50 cursor-not-allowed">
                        <ThumbsUp size={16} />
                        <ThumbsDown size={16} />
                      </div>
                    </div>
                  )}
                  {expanded === i && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                        {JSON.stringify(m.fullResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <ProcessingAnimation status={processingStatus} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white p-6">
          <div className="max-w-3xl mx-auto">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={agreed ? 'Type your message here…' : "Type 'Agree' or 'Disagree'"}
                disabled={UNDER_MAINTENANCE}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${UNDER_MAINTENANCE && 'opacity-50 cursor-not-allowed'}"
              />
              <button
                type="submit"
                disabled={UNDER_MAINTENANCE}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${UNDER_MAINTENANCE && 'opacity-50 cursor-not-allowed'}"
              >
                <Send size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotInterface;
